import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { GoBackButton } from '@shared/ui/atoms/buttons/GoBackButton';
import { useSelector } from "react-redux";
import { selectAllOfficeEntities } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { OfficeEntity } from '@features/desks/services/AdvanceHotDeskingService';
import Select from "react-select";
import { REACT_SELECT_DROPDOWN_STYLES } from '@shared/ui/organisms/OfficeDropDownList';
import { DateRangePicker } from '@shared/ui/molecules/DateRangePicker';
import {
  DeskBookingSchedule,
  DeskUtilizationDetail,
  DeskUtilizationReport,
  FloorDeskUtilization,
  getDeskBookingSchedule,
  getDeskUtilizationForOffice
} from '@features/reporting/services/DeskUtilizationService';
import { LoadingSpinner } from '@shared/ui/atoms/LoadingSpinner';
import { Center } from '@shared/ui/atoms/StructuralLayout';
import { BodyLarge, BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { OutlineButton } from '@shared/ui/atoms/buttons/OutlineButton';
import { HeadlineLarge } from '@shared/ui/atoms/fonts/Headline';
import Colours from '@shared/ui/atoms/Colours';
import { MountedContentCard, MountedContentCardContent } from '@shared/ui/atoms/MountedContentCard';
import { Moment } from "moment";
import moment from "moment";
import { tablet } from '@shared/ui/atoms/MediaQueries';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CustomToolTip } from "../components/CustomToolTip";
import { FlatContentCard } from '@shared/ui/atoms/FlatContentCard';
import { ButtonSize } from '@shared/ui/atoms/buttons/ButtonStyling';

const COMPANY_WIDE_ID = -1;

type SortColumn = 'floor' | 'desks' | 'bookings' | 'days' | 'utilization';
type SortDirection = 'asc' | 'desc';

export function DeskUtilizationReportingPage(props: Props) {
  const {t} = useTranslation();
  const offices = useSelector(selectAllOfficeEntities);
  const rootOffices = useMemo(() => offices.filter((o: OfficeEntity) => !o.parentId), [offices]);

  const officeOptions = useMemo(() => {
    const companyWide = { value: COMPANY_WIDE_ID, label: t('reports-page.desk-utilization-company-wide') };
    const officeItems = rootOffices.map((o: OfficeEntity) => ({ value: o.id, label: o.label }));
    return [companyWide, ...officeItems];
  }, [rootOffices, t]);

  const [selectedOption, setSelectedOption] = useState<{ value: number; label: string } | null>(null);
  const office = useMemo(() => {
    if (!selectedOption || selectedOption.value === COMPANY_WIDE_ID) return undefined;
    return rootOffices.find((o: OfficeEntity) => o.id === selectedOption.value);
  }, [selectedOption, rootOffices]);
  const [fromDate, setFromDate] = useState<Moment>(moment().startOf('month'));
  const [toDate, setToDate] = useState<Moment>(moment().endOf('month'));
  const [reportData, setReportData] = useState<DeskUtilizationReport | undefined>();
  const [officeReports, setOfficeReports] = useState<DeskUtilizationReport[]>([]);
  const [loading, setLoading] = useState(false);
  const [expandedFloors, setExpandedFloors] = useState<Set<number>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [sortColumn, setSortColumn] = useState<SortColumn>('floor');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [collapsedOffices, setCollapsedOffices] = useState<Set<number>>(new Set());

  const isCompanyWide = selectedOption?.value === COMPANY_WIDE_ID;

  useEffect(() => {
    if (!selectedOption && officeOptions.length > 0) {
      setSelectedOption(officeOptions[0]);
    }
  }, [officeOptions, selectedOption]);

  useEffect(() => {
    if (selectedOption && fromDate && toDate) {
      setLoading(true);
      setReportData(undefined);
      setOfficeReports([]);
      if (isCompanyWide) {
        const companyWidePromise = getDeskUtilizationForOffice(undefined, fromDate, toDate);
        const officePromises = rootOffices.map((o: OfficeEntity) =>
          getDeskUtilizationForOffice(o, fromDate, toDate)
        );
        Promise.all([companyWidePromise, Promise.all(officePromises)])
          .then(([companyData, perOfficeData]) => {
            setReportData(companyData);
            setOfficeReports(perOfficeData.filter(r => r.floors.length > 0));
          })
          .finally(() => setLoading(false));
      } else {
        getDeskUtilizationForOffice(office, fromDate, toDate)
          .then(setReportData)
          .finally(() => setLoading(false));
      }
    }
  }, [selectedOption, fromDate, toDate, isCompanyWide, office, rootOffices]);

  const onDatesSelected = (from?: Moment, to?: Moment) => {
    if (from) setFromDate(from);
    if (to) setToDate(to);
  };

  const downloadCsv = async () => {
    if (!fromDate || !toDate) return;
    setDownloading(true);
    try {
      const data: any = await getDeskBookingSchedule(isCompanyWide ? undefined : office, fromDate, toDate);
      const schedules = data as DeskBookingSchedule[];
      if (!schedules || schedules.length === 0) return;

      const allDates = new Set<string>();
      schedules.forEach(desk => desk.bookings.forEach(b => allDates.add(b.date)));
      const sortedDates = Array.from(allDates).sort();

      const header = [t('reports-page.desk-utilization-floor'), t('reports-page.desk-utilization-desks'), ...sortedDates.map(d => moment(d).format('DD/MM/YYYY'))];
      const rows = schedules.map(desk => {
        const bookingMap = new Map(desk.bookings.map(b => [b.date, b]));
        const cells = sortedDates.map(date => {
          const booking = bookingMap.get(date);
          if (!booking) return '0';
          if (booking.am && booking.pm) return '1';
          if (booking.am || booking.pm) return '0.5';
          return '0';
        });
        return [desk.floorLabel, desk.label, ...cells];
      });

      const csvContent = [header, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `desk-utilization-${fromDate.format('YYYY-MM-DD')}-to-${toDate.format('YYYY-MM-DD')}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  };

  const floorChartData = useMemo(() => {
    if (!reportData) return [];
    return reportData.floors.map(floor => ({
      name: floor.floorLabel,
      utilization: floor.utilizationPercentage,
      bookings: floor.totalBookings,
      desks: floor.totalDesks,
      toolTipTitle: floor.floorLabel,
    }));
  }, [reportData]);

  const deskChartData = useMemo(() => {
    if (!reportData) return [];
    if (isCompanyWide) {
      return reportData.floors
        .map(floor => ({
          name: floor.floorLabel,
          utilization: floor.utilizationPercentage,
          bookings: floor.totalBookings,
          desks: floor.totalDesks,
          toolTipTitle: floor.floorLabel,
        }))
        .sort((a, b) => a.utilization - b.utilization)
        .slice(0, 10);
    }
    const allDesks = reportData.floors.flatMap(floor =>
      floor.desks.map(desk => ({
        name: desk.label,
        floor: floor.floorLabel,
        utilization: desk.daysInRange > 0 ? Math.round((desk.usage / desk.daysInRange) * 100) : 0,
        usage: desk.usage,
        daysInRange: desk.daysInRange,
        toolTipTitle: desk.label,
      }))
    );
    return allDesks.sort((a, b) => a.utilization - b.utilization).slice(0, 10);
  }, [reportData, isCompanyWide]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortFloorData = useCallback((floors: FloorDeskUtilization[]) => {
    const sorted = [...floors];
    const dir = sortDirection === 'asc' ? 1 : -1;
    return sorted.sort((a, b) => {
      switch (sortColumn) {
        case 'floor': return dir * a.floorLabel.localeCompare(b.floorLabel);
        case 'desks': return dir * (a.totalDesks - b.totalDesks);
        case 'bookings': return dir * (a.totalBookings - b.totalBookings);
        case 'days': return dir * (a.daysInRange - b.daysInRange);
        case 'utilization': return dir * (a.utilizationPercentage - b.utilizationPercentage);
        default: return 0;
      }
    });
  }, [sortDirection, sortColumn]);

  const sortDeskData = (desks: DeskUtilizationDetail[]) => {
    const sorted = [...desks];
    const dir = sortDirection === 'asc' ? 1 : -1;
    return sorted.sort((a, b) => {
      switch (sortColumn) {
        case 'floor': return dir * a.label.localeCompare(b.label);
        case 'bookings': return dir * (a.usage - b.usage);
        case 'days': return dir * (a.daysInRange - b.daysInRange);
        case 'utilization': {
          const aUtil = a.daysInRange > 0 ? a.usage / a.daysInRange : 0;
          const bUtil = b.daysInRange > 0 ? b.usage / b.daysInRange : 0;
          return dir * (aUtil - bUtil);
        }
        default: return 0;
      }
    });
  };

  const sortedFloors = useMemo(() => {
    if (!reportData) return [];
    return sortFloorData(reportData.floors);
  }, [reportData, sortFloorData]);

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' \u25B2' : ' \u25BC';
  };

  const toggleOffice = (floorId: number) => {
    setCollapsedOffices(prev => {
      const next = new Set(prev);
      if (next.has(floorId)) {
        next.delete(floorId);
      } else {
        next.add(floorId);
      }
      return next;
    });
  };

  const toggleFloor = (floorId: number) => {
    setExpandedFloors(prev => {
      const next = new Set(prev);
      if (next.has(floorId)) {
        next.delete(floorId);
      } else {
        next.add(floorId);
      }
      return next;
    });
  };

  return (
    <>
      <ReportingContainer>
        <GoBackButton url={"/manage/reporting"} />
        <HeadlineLarge style={{marginBottom: 16}}>{t('reports-page.desk-utilization-report-title')}</HeadlineLarge>

        <ControlsRow>
          <ControlGroup>
            <BodyRegular weight={600} style={{marginBottom: 8}}>{t('reports-page.desk-utilization-office')}</BodyRegular>
            <Select options={officeOptions}
                    value={selectedOption}
                    onChange={(item: any) => setSelectedOption(item)}
                    styles={REACT_SELECT_DROPDOWN_STYLES}
                    isDisabled={loading}
                    isSearchable={false} />
          </ControlGroup>
          <DateRangeGroup>
            <DateRangePicker disabled={loading}
                             onDatesSelected={onDatesSelected}
                             fromDate={fromDate}
                             toDate={toDate} />
          </DateRangeGroup>
        </ControlsRow>

        {loading && (
          <Center>
            <LoadingSpinner hideBorder={true} />
          </Center>
        )}

        {!loading && !reportData && selectedOption && (
          <Center style={{marginTop: 32}}>
            <MountedContentCard>
              <MountedContentCardContent>
                <BodyLarge weight={600} colour={Colours.blue} style={{marginBottom: 12}}>{t('reports-page.desk-utilization-no-data')}</BodyLarge>
                <BodyRegular>{t('reports-page.desk-utilization-no-data-description')}</BodyRegular>
              </MountedContentCardContent>
            </MountedContentCard>
          </Center>
        )}

        {!loading && reportData && (
          <>
            <SummaryCard>
              <SummaryTitle>{reportData.officeLabel}</SummaryTitle>
              <SummaryGrid>
                <SummaryItem>
                  <SummaryValue>{reportData.totalDesks}</SummaryValue>
                  <SummaryLabel>{t('reports-page.desk-utilization-total-desks')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.totalBookings}</SummaryValue>
                  <SummaryLabel>{t('reports-page.desk-utilization-total-bookings')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.daysInRange}</SummaryValue>
                  <SummaryLabel>{t('reports-page.desk-utilization-days-in-range')}</SummaryLabel>
                </SummaryItem>
                <SummaryItem>
                  <SummaryValue>{reportData.utilizationPercentage}%</SummaryValue>
                  <SummaryLabel>{t('reports-page.desk-utilization-utilization')}</SummaryLabel>
                </SummaryItem>
              </SummaryGrid>
            </SummaryCard>

            <ChartsRow>
              {floorChartData.length > 0 && (
                <FlatContentCard>
                  <BodyLarge weight={600} style={{marginBottom: 12}}>{t(isCompanyWide ? 'reports-page.desk-utilization-by-office' : 'reports-page.desk-utilization-by-floor')}</BodyLarge>
                  <div style={{height: 350}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={floorChartData}
                                margin={{top: 20, right: 20, left: 0, bottom: 5}}>
                        <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" />
                        <XAxis dataKey="name"
                               fontFamily={'"soleil", sans-serif'}
                               fill={Colours.darkGrey}
                               fontSize={12}
                               axisLine={false}
                               tickLine={false} />
                        <YAxis fontFamily={'"soleil", sans-serif'}
                               fontSize={12}
                               axisLine={false}
                               tickLine={false}
                               domain={[0, 100]}
                               tickFormatter={(v) => `${v}%`} />
                        <Tooltip content={<CustomToolTip />} />
                        <Bar dataKey="utilization" fill={Colours.blue} radius={[4, 4, 0, 0]}>
                          {floorChartData.map((entry, index) => (
                            <Cell key={index} fill={Colours.green} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </FlatContentCard>
              )}

              {deskChartData.length > 0 && (
                <FlatContentCard>
                  <BodyLarge weight={600} style={{marginBottom: 12}}>{t(isCompanyWide ? 'reports-page.desk-utilization-top-offices' : 'reports-page.desk-utilization-top-desks')}</BodyLarge>
                  <div style={{height: 350}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={deskChartData} layout="vertical"
                                margin={{top: 10, right: 20, left: 0, bottom: 5}}>
                        <CartesianGrid stroke={Colours.lightGrey} strokeDasharray="5 5" horizontal={false} />
                        <XAxis type="number"
                               fontFamily={'"soleil", sans-serif'}
                               fontSize={12}
                               axisLine={false}
                               tickLine={false}
                               domain={[0, 100]}
                               tickFormatter={(v) => `${v}%`} />
                        <YAxis type="category"
                               dataKey="name"
                               fontFamily={'"soleil", sans-serif'}
                               fontSize={11}
                               axisLine={false}
                               tickLine={false}
                               width={70} />
                        <Tooltip content={<CustomToolTip />} />
                        <Bar dataKey="utilization" fill={Colours.blue} radius={[0, 4, 4, 0]}>
                          {deskChartData.map((entry, index) => (
                            <Cell key={index} fill={Colours.green} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </FlatContentCard>
              )}
            </ChartsRow>

            {isCompanyWide && officeReports.map((officeReport) => (
              <FloorSection key={officeReport.officeId}>
                <OfficeSectionHeader onClick={() => toggleOffice(officeReport.officeId)}>
                  <Arrow expanded={!collapsedOffices.has(officeReport.officeId)}>&#9654;</Arrow>
                  <BodyLarge weight={600}>{officeReport.officeLabel}</BodyLarge>
                  <BodySmall colour={Colours.darkGrey}>
                    {officeReport.totalDesks} {t('reports-page.desk-utilization-desks').toLowerCase()} &middot; {officeReport.utilizationPercentage}% {t('reports-page.desk-utilization-utilization').toLowerCase()}
                  </BodySmall>
                </OfficeSectionHeader>
                {!collapsedOffices.has(officeReport.officeId) && officeReport.floors.map((floor: FloorDeskUtilization) => (
                  <FloorTableSection key={floor.floorId}>
                    <FloorTableHeader onClick={() => toggleFloor(floor.floorId)}>
                      <Arrow expanded={expandedFloors.has(floor.floorId)}>&#9654;</Arrow>
                      <BodyRegular weight={600}>{floor.floorLabel}</BodyRegular>
                      <BodySmall colour={Colours.darkGrey}>
                        {floor.totalDesks} {t('reports-page.desk-utilization-desks').toLowerCase()} &middot; {floor.utilizationPercentage}% {t('reports-page.desk-utilization-utilization').toLowerCase()}
                      </BodySmall>
                    </FloorTableHeader>
                    {expandedFloors.has(floor.floorId) && (
                      <Table>
                        <thead>
                          <tr>
                            <SortableTh onClick={() => handleSort('floor')}>{t('reports-page.desk-utilization-desks')}{sortIndicator('floor')}</SortableTh>
                            <SortableTh onClick={() => handleSort('bookings')}>{t('reports-page.desk-utilization-bookings')}{sortIndicator('bookings')}</SortableTh>
                            <SortableTh onClick={() => handleSort('days')}>{t('reports-page.desk-utilization-days')}{sortIndicator('days')}</SortableTh>
                            <SortableTh onClick={() => handleSort('utilization')}>{t('reports-page.desk-utilization-utilization')}{sortIndicator('utilization')}</SortableTh>
                          </tr>
                        </thead>
                        <tbody>
                          {sortDeskData(floor.desks).map(desk => (
                            <tr key={desk.deskId}>
                              <Td>{desk.label}</Td>
                              <Td>{desk.usage}</Td>
                              <Td>{desk.daysInRange}</Td>
                              <Td>{desk.daysInRange > 0 ? Math.round((desk.usage / desk.daysInRange) * 100) : 0}%</Td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </FloorTableSection>
                ))}
              </FloorSection>
            ))}

            {reportData.floors.length > 0 && !isCompanyWide && (
              sortedFloors.map((floor: FloorDeskUtilization) => (
                <FloorSection key={floor.floorId}>
                  <FloorTableHeader onClick={() => toggleFloor(floor.floorId)}>
                    <Arrow expanded={expandedFloors.has(floor.floorId)}>&#9654;</Arrow>
                    <BodyRegular weight={600}>{floor.floorLabel}</BodyRegular>
                    <BodySmall colour={Colours.darkGrey}>
                      {floor.totalDesks} {t('reports-page.desk-utilization-desks').toLowerCase()} &middot; {floor.utilizationPercentage}% {t('reports-page.desk-utilization-utilization').toLowerCase()}
                    </BodySmall>
                  </FloorTableHeader>
                  {expandedFloors.has(floor.floorId) && (
                    <Table>
                      <thead>
                        <tr>
                          <SortableTh onClick={() => handleSort('floor')}>{t('reports-page.desk-utilization-desks')}{sortIndicator('floor')}</SortableTh>
                          <SortableTh onClick={() => handleSort('bookings')}>{t('reports-page.desk-utilization-bookings')}{sortIndicator('bookings')}</SortableTh>
                          <SortableTh onClick={() => handleSort('days')}>{t('reports-page.desk-utilization-days')}{sortIndicator('days')}</SortableTh>
                          <SortableTh onClick={() => handleSort('utilization')}>{t('reports-page.desk-utilization-utilization')}{sortIndicator('utilization')}</SortableTh>
                        </tr>
                      </thead>
                      <tbody>
                        {sortDeskData(floor.desks).map(desk => (
                          <tr key={desk.deskId}>
                            <Td>{desk.label}</Td>
                            <Td>{desk.usage}</Td>
                            <Td>{desk.daysInRange}</Td>
                            <Td>{desk.daysInRange > 0 ? Math.round((desk.usage / desk.daysInRange) * 100) : 0}%</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </FloorSection>
              ))
            )}

            <FlatContentCard style={{marginTop: 32}}>
              <BodyLarge weight={600} style={{marginBottom: 4}}>{t('reports-page.desk-utilization-download-title')}</BodyLarge>
              <BodyRegular colour={Colours.darkGrey} style={{marginBottom: 12}}>{t('reports-page.desk-utilization-download-description')}</BodyRegular>
              <OutlineButton text="button.download-csv"
                             size={ButtonSize.Medium}
                             disabled={downloading}
                             click={downloadCsv} />
            </FlatContentCard>
          </>
        )}
      </ReportingContainer>
    </>
  );
}

interface Props {
}

const ReportingContainer = styled.div`
  max-width: 100%;
  min-height: 100vh;
  background-color: white;
  padding: 24px;
`

const ControlsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
  @media (${tablet}) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`

const ControlGroup = styled.div`
  width: 100%;
  @media (${tablet}) {
    max-width: 320px;
  }
`

const DateRangeGroup = styled.div`
  width: 100%;
  .textField {
    margin-bottom: 0 !important;
  }
  @media (${tablet}) {
    max-width: 400px;
  }
`


const SummaryCard = styled.div`
  background-color: ${Colours.blue10};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
`

const SummaryTitle = styled.h3`
  font-family: "soleil", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: ${Colours.black};
  margin: 0 0 16px 0;
`

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  @media (${tablet}) {
    grid-template-columns: repeat(4, 1fr);
  }
`

const SummaryItem = styled.div`
  text-align: center;
`

const SummaryValue = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: ${Colours.blue};
`

const SummaryLabel = styled.div`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  color: ${Colours.darkGrey};
  margin-top: 4px;
`

const ChartsRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 32px;
  @media (${tablet}) {
    flex-direction: row;
    & > * {
      flex: 1;
    }
  }
`


const FloorSection = styled.div`
  margin-top: 8px;
`

const OfficeSectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 24px 0 8px 0;
  cursor: pointer;
  user-select: none;
`

const FloorTableSection = styled.div`
  margin-left: 16px;
  margin-bottom: 8px;
`

const FloorTableHeader = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 12px 0 4px 0;
  cursor: pointer;
  user-select: none;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`

const SortableTh = styled.th`
  font-family: "soleil", sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: ${Colours.darkGrey};
  text-align: left;
  padding: 8px 12px;
  border-bottom: 2px solid ${Colours.mildGrey};
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  &:hover {
    color: ${Colours.black};
  }
`

const Td = styled.td`
  font-family: "soleil", sans-serif;
  font-size: 14px;
  color: ${Colours.black};
  padding: 10px 12px;
  border-bottom: 1px solid ${Colours.mildGrey};
`

const Arrow = styled.span<{ expanded: boolean }>`
  display: inline-block;
  font-size: 10px;
  transition: transform 0.2s;
  transform: ${props => props.expanded ? 'rotate(90deg)' : 'rotate(0deg)'};
`
